import { useMemo } from 'react';

import { Box, Skeleton } from '@chakra-ui/react';
import { MetricCards } from 'Deer/metricCards/MetricCards';
import {
  DISCIPLINARY_ACTIONS_ADMIN_REPORTS_METRIC_CARDS,
  MODULE_KEYS_METRIC_CARDS_DEER,
} from 'Deer/metricCards/constants';
import { StyledPaper, StyledTypography } from 'styles/common';
import { checkEnableCompanySupport } from 'utils/helpers/featureFlags';

import {
  useSelectDisciplinaryActionsSelectedCompanies,
  useSelectDisciplinaryActionsSelectedLocations,
} from '../slice/DisciplinaryActionsIntentSlice';

export const AdminReportMetricCard = ({ data, isLoading, error, gridStatePersistenceKey }) => {
  const rows = data?.entity?.rows;
  const selectedCompanies = useSelectDisciplinaryActionsSelectedCompanies();
  const selectedLocations = useSelectDisciplinaryActionsSelectedLocations();

  const filteredRows = useMemo(() => {
    if (checkEnableCompanySupport()) {
      const selectedCompaniesMap = {};
      const selectedLocationsMap = {};
      selectedCompanies?.forEach((company) => {
        selectedCompaniesMap[company.id] = company;
      });
      selectedLocations?.forEach((location) => {
        selectedLocationsMap[location?.toLowerCase()] = location;
      });

      return rows?.filter(
        (row) => selectedCompaniesMap[row?.COMPANY?.id] && selectedLocationsMap[row?.LOCATION?.toLowerCase()]
      );
    }
    return rows;
  }, [rows, selectedCompanies, selectedLocations]);

  const {
    employeeWithViolationCount,
    highSeverityViolationCount,
    greaterThanThreeViolationCount,
    pendingAcknowledgementCount,
  } = useMemo(() => {
    const employeesWithViolation =
      filteredRows?.reduce((acc, rowData) => {
        if (rowData?.TOTAL_VIOLATIONS > 0) {
          return acc + 1;
        }
        return acc;
      }, 0) || 0;

    const employeesWithHighSeverityViolation =
      filteredRows?.reduce((acc, rowData) => {
        if (!rowData?.VIOLATIONS_BY_SEVERITY?.length) return acc;

        const hasHighSeverityViolation = rowData.VIOLATIONS_BY_SEVERITY.some(
          (violation) => violation?.label?.toLowerCase() === 'high' && (violation?.value ?? 0) > 0
        );

        return hasHighSeverityViolation ? acc + 1 : acc;
      }, 0) || 0;

    const employeesWithGreaterThanThreeViolation =
      filteredRows?.reduce((acc, rowData) => {
        if (rowData?.TOTAL_VIOLATIONS > 3) {
          return acc + 1;
        }
        return acc;
      }, 0) || 0;

    const employeesWithPendingAcknowledgement =
      filteredRows?.reduce((acc, rowData) => {
        if (rowData?.UNACKNOWLEDGED_NOTES > 0) {
          return acc + 1;
        }
        return acc;
      }, 0) || 0;

    return {
      employeeWithViolationCount: employeesWithViolation,
      highSeverityViolationCount: employeesWithHighSeverityViolation,
      greaterThanThreeViolationCount: employeesWithGreaterThanThreeViolation,
      pendingAcknowledgementCount: employeesWithPendingAcknowledgement,
    };
  }, [filteredRows]);

  const cardKPIData = [
    {
      cardId: DISCIPLINARY_ACTIONS_ADMIN_REPORTS_METRIC_CARDS.employeesWithViolations,
      title: 'Employees with violations',
      value: employeeWithViolationCount,
    },
    {
      cardId: DISCIPLINARY_ACTIONS_ADMIN_REPORTS_METRIC_CARDS.highSeverityViolations,
      title: 'Employees with high severity violations',
      value: highSeverityViolationCount,
    },
    {
      cardId: DISCIPLINARY_ACTIONS_ADMIN_REPORTS_METRIC_CARDS.greaterThanThreeViolations,
      title: 'Employees with > 3 violations',
      value: greaterThanThreeViolationCount,
    },
    {
      cardId: DISCIPLINARY_ACTIONS_ADMIN_REPORTS_METRIC_CARDS.pendingAcknowledgement,
      title: 'Employees with pending acknowledgement',
      value: pendingAcknowledgementCount,
    },
  ];

  if (isLoading) {
    return (
      <Box display="flex">
        <Skeleton height="100px" width="100%" borderRadius="8px" startColor="surface.F5F5F5" endColor="surface.g3" />
      </Box>
    );
  }

  if (error) {
    return (
      <StyledPaper padding="1rem" topGutter={3} elevation={0} showBorder>
        <StyledTypography variant="body2" color="red" align="center">
          Error occurred while fetching metric data
        </StyledTypography>
      </StyledPaper>
    );
  }

  return (
    <MetricCards
      height="100%"
      columns={`repeat(${cardKPIData?.length}, 1fr)`}
      cards={cardKPIData}
      module={MODULE_KEYS_METRIC_CARDS_DEER.DISCIPLINARY_ACTIONS}
      gridStatePersistenceKey={gridStatePersistenceKey}
      cardTitle="Metrics"
      cardStyle="style1"
    />
  );
};
